import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useKibanaLogger } from '../../../shared/hooks/kibana-logger/kibana-logger';
import {
  getDataBuilderSettings,
  updateDataBuilderSettings,
} from '../../DataBuilder/api/data-builder-api';
import type {
  DataBuilderSettingsGroup,
  DataBuilderUpdateSettingsPostBody,
} from '../../DataBuilder/api/data-builder-api.types';
import { withDataBuilderMutationPolicy } from '../../DataBuilder/api/data-builder-request-policies';
import {
  buildSettingsUpdatePayload,
  type SectionMeta,
  seedValues,
} from './helpers/settings-payload';
import type { SettingsValue, SettingsValues } from './helpers/settings-value';

export type UseDataBuilderSettingsResult = {
  groups: DataBuilderSettingsGroup[];
  values: SettingsValues;
  setValue: (key: string, value: SettingsValue) => void;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isSaving: boolean;
  saveSettings: (
    clientName: string,
    valuesOverride?: SettingsValues
  ) => Promise<void>;
};

export const useDataBuilderSettings = ({
  clientName,
}: {
  clientName: string | null | undefined;
}): UseDataBuilderSettingsResult => {
  const { kibanaLogger } = useKibanaLogger();
  const queryClient = useQueryClient();

  const queryEnabled = Boolean(clientName);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['data-builder-settings', clientName],
    queryFn: () => getDataBuilderSettings({ client_name: clientName ?? '' }),
    enabled: queryEnabled,
  });

  const [values, setValues] = useState<SettingsValues>({});
  const seededClientRef = useRef<string | null>(null);

  useEffect(() => {
    if (!data) {
      return;
    }
    // Seed once per loaded client so a background refetch / name change doesn't
    // clobber in-progress edits.
    if (seededClientRef.current === data.client_name) {
      return;
    }
    seededClientRef.current = data.client_name;
    setValues(seedValues(data.groups));
  }, [data]);

  const setValue = useCallback((key: string, value: SettingsValue) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  // section key -> { type, required }, so buildSettingsUpdatePayload can apply
  // the ""→null rule for non-required text fields.
  const sectionByKey = useMemo(() => {
    const map: Record<string, SectionMeta> = {};
    for (const group of data?.groups ?? []) {
      for (const section of group.sections) {
        map[section.key] = { type: section.type, required: section.required };
      }
    }
    return map;
  }, [data]);

  const { mutateAsync, isPending: isSaving } = useMutation<
    void,
    Error,
    DataBuilderUpdateSettingsPostBody
  >(
    withDataBuilderMutationPolicy(
      {
        mutationKey: ['data-builder-settings', 'update'],
        mutationFn: updateDataBuilderSettings,
      },
      kibanaLogger
    )
  );

  const saveSettings = useCallback(
    async (name: string, valuesOverride?: SettingsValues) => {
      await mutateAsync(
        buildSettingsUpdatePayload(name, valuesOverride ?? values, sectionByKey)
      );
      seededClientRef.current = null;
      await queryClient.invalidateQueries({
        queryKey: ['data-builder-settings', name],
      });
    },
    [mutateAsync, values, sectionByKey, queryClient]
  );

  return {
    groups: data?.groups ?? [],
    values,
    setValue,
    isLoading,
    isError,
    error,
    isSaving,
    saveSettings,
  };
};
